import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter, WithRouterProps } from 'react-router';
import StatusCode from 'react-nested-status';

import { Language } from 'ha/i18n';
import { OTHER } from 'ha/constants/pageNames';
import { onPageMount } from 'ha/modules/Analytics/actions';
import { getAvailableLanguages } from 'ha/modules/LanguageSwitcher/selectors';
import { getMediaQueryBreakpoints } from 'ha/modules/UI/selectors';
import { FontInfo } from 'ha/components/LoadFonts';
import {
  getCurrentLanguage,
  getIsAuthenticated,
} from 'ha/modules/AuthLogic/selectors';
import { GlobalState } from 'ha/types/store';
import { withConfig, ConfigProps } from 'ha/helpers/config';
import CSSBundle from 'ha/helpers/CSSBundle';
import reportError from 'ha/helpers/bugsnag/reportError';
import loadNonCriticalData from 'ha/utils/dom/loadNonCriticalData';

import PageLayout from '../components/PageLayout';
import { PageProvider } from '../contexts/PageContext';

interface Props {
  availableLanguages: Array<{
    code: Language;
    localisedText: string;
  }>;
  noFollowFooter?: boolean;
  banner?: React.ReactNode;
  breadcrumbs?: React.ReactElement<{ withoutFirstSeparator: boolean }> | null;
  category?: string;
  hasFooter?: boolean;
  isLandingPage?: boolean;
  name: string;
  remarketingTag?: object;
  statusCode?: number;
  traits?: object;
  mediaQueryBreakpoints: {
    lg: boolean;
  };
  hideIntercom?: boolean;
  onPageMount: (properties: object) => void;
  language: string;
  excludeLegacyCSSBundle?: boolean;
  includeLegacyIconFont?: boolean;
  isAuthenticated: boolean;
  activeFonts?: FontInfo[];
}

const enhance = connect(
  (state: GlobalState) => ({
    availableLanguages: getAvailableLanguages(state),
    mediaQueryBreakpoints: getMediaQueryBreakpoints(state),
    language: getCurrentLanguage(state),
    isAuthenticated: getIsAuthenticated(state),
  }),
  {
    onPageMount,
  },
);

export class Page extends React.Component<
  Props & ConfigProps & WithRouterProps
> {
  static defaultProps = {
    category: OTHER,
    hasFooter: true,
    hideIntercom: false,
    isLandingPage: false,
    noFollowFooter: true,
    traits: {},
  };

  componentDidMount() {
    const {
      appConfig,
      category,
      hideIntercom,
      language,
      mediaQueryBreakpoints: { lg },
      name,
      remarketingTag,
      traits,
      includeLegacyIconFont,
    } = this.props;

    this.props.onPageMount({
      category,
      config: appConfig,
      hideIntercom: hideIntercom || !lg,
      location: this.props.location,
      name,
      // tslint:disable-next-line no-any
      referrer: (global as any).document.referrer,
      remarketingTag,
      traits: { ...traits, language },
    });

    if (includeLegacyIconFont) {
      loadNonCriticalData(() => {
        import(
          /* webpackChunkName: "font-awesome" */ 'font-awesome/css/font-awesome.css'
        ).catch(error => reportError(error));
      });
    }
  }

  render() {
    const {
      availableLanguages,
      banner,
      breadcrumbs,
      children,
      excludeLegacyCSSBundle,
      hasFooter = true,
      isLandingPage,
      noFollowFooter = true,
      statusCode = 200,
      isAuthenticated,
      activeFonts,
      name,
      category,
    } = this.props;

    return (
      <CSSBundle excludeBundle={excludeLegacyCSSBundle}>
        <StatusCode code={statusCode}>
          <PageProvider name={name} category={category}>
            <PageLayout
              availableLanguages={isLandingPage ? availableLanguages : []}
              banner={banner}
              breadcrumbs={breadcrumbs}
              hasFooter={hasFooter}
              noFollowFooter={noFollowFooter}
              isAuthenticated={isAuthenticated}
              activeFonts={activeFonts}
            >
              {children}
            </PageLayout>
          </PageProvider>
        </StatusCode>
      </CSSBundle>
    );
  }
}

export default enhance(withConfig(withRouter(Page)));
